import { SessionExpiredError } from './types.js';
/** A cached token is refreshed this long before it expires, so a request never carries a dying token. */
export const DEV_TOKEN_REFRESH_MARGIN_MS = 30_000;
function httpStatus(error) {
    if (typeof error !== 'object' || error === null)
        return undefined;
    const status = error.status;
    return typeof status === 'number' ? status : undefined;
}
/**
 * Development identity: a clearly labeled local substitute for the identity provider. The API keeps the
 * session in an HttpOnly cookie; this class only asks it for short-lived access tokens per audience and
 * keeps them in memory. It never backs a Microsoft, Google or SSO button.
 */
export class DevelopmentSession {
    mode = 'development';
    config;
    tokens = new Map();
    pending = new Map();
    constructor(config) {
        this.config = config;
    }
    now() {
        return this.config.now?.() ?? Date.now();
    }
    listFixtures() {
        return this.config.client.devFixtures();
    }
    /** Starts a development session as the fixture. Any token of a previous fixture is dropped first. */
    async signIn(fixtureKey) {
        this.tokens.clear();
        this.pending.clear();
        await this.config.client.devStartSession(fixtureKey);
    }
    /**
     * A token for the audience; with options.contextId an organization-scoped one for that context (the API
     * refuses a context the fixture does not hold: 403, or 403 sso_required with sign-in hints). Tokens are
     * cached per audience and context, never across them.
     */
    getAccessToken(audience = this.config.defaultAudience, options = {}) {
        const { contextId } = options;
        const key = JSON.stringify([audience, contextId ?? null]);
        const cached = this.tokens.get(key);
        if (cached && this.now() < cached.expiresAt - DEV_TOKEN_REFRESH_MARGIN_MS)
            return Promise.resolve(cached.token);
        // Concurrent callers share one token request per audience and context.
        const inFlight = this.pending.get(key);
        if (inFlight)
            return inFlight;
        const request = this.fetchToken(audience, contextId, key).finally(() => this.pending.delete(key));
        this.pending.set(key, request);
        return request;
    }
    async fetchToken(audience, contextId, key) {
        const requestedAt = this.now();
        try {
            const response = contextId === undefined
                ? await this.config.client.devToken(audience)
                : await this.config.client.devToken(audience, { contextId });
            this.tokens.set(key, { token: response.accessToken, expiresAt: requestedAt + response.expiresIn * 1000 });
            return response.accessToken;
        }
        catch (error) {
            this.tokens.delete(key);
            if (httpStatus(error) === 401)
                throw new SessionExpiredError('login_required', { cause: error });
            throw error;
        }
    }
    async isAuthenticated() {
        try {
            await this.getAccessToken();
            return true;
        }
        catch (error) {
            // 401: no development session; 404: development identity is not enabled on this API.
            if (error instanceof SessionExpiredError || httpStatus(error) === 404)
                return false;
            throw error;
        }
    }
    /**
     * Ends the development session, forgets every token and loads returnTo. When the API cannot end the
     * session the error propagates and nothing navigates, so the page never claims a sign-out that did not
     * happen. A session that is already gone (401) or an API without development identity (404) counts as
     * signed out.
     */
    async signOut(returnTo) {
        this.tokens.clear();
        this.pending.clear();
        try {
            await this.config.client.devEndSession();
        }
        catch (error) {
            const status = httpStatus(error);
            if (status !== 401 && status !== 404)
                throw error;
        }
        (this.config.assign ?? ((url) => window.location.assign(url)))(returnTo);
    }
}
//# sourceMappingURL=development-session.js.map